/**
 * Switch.js
 *
 * The toggle control of the Lantern design system (ChoreStory). It mirrors the
 * mockups' `<span data-component="Switch" class="switch">` (Settings),
 * `<label class="switch">` (Onboarding) and `<button class="switch">`
 * (Quest Studio) — see redesign/mockups/{settings,onboarding,quest-studio}.html.
 * Used on the `settings`, `onboarding` and `quest-studio` screens for calm,
 * never-punitive preferences ("Approval required", "Weekend grace", "Sounds").
 *
 *     Approval required                              ┌────────●┐   ← on  (thumb right)
 *     Completions wait for a high-five      on       └─────────┘
 *
 *     Sounds — kiosk                                 ┌●────────┐   ← off (thumb left)
 *     Off by default; the kitchen stays quiet  off   └─────────┘
 *
 * Built on a NATIVE `<input type="checkbox" role="switch">` (not a div-with-onClick)
 * so keyboard (Space), focus and labelling come for free and stay correct — the
 * Settings/Onboarding mockups use exactly this. axe's aria-required-attr maps the
 * native `checked` state to aria-checked, so no manual aria-checked is needed.
 *
 * Works controlled (`checked` + `onChange`/`onCheckedChange`) or uncontrolled
 * (`defaultChecked`). The visible row label/description are optional — pass them
 * for a self-describing, accessibly-named control, or render the bare toggle and
 * give it an `ariaLabel`.
 *
 * States (the per-screen spec flags error, hover, loading as MISSING for Switch —
 * all are defined honestly here):
 *   - loading  the control collapses to a calm shimmer pill; the root is aria-busy
 *              and a polite live region announces it. Never a spinner.
 *   - empty    when there is no setting to show (`empty`): a calm placeholder line,
 *              never a blank render.
 *   - error    a CALM note below the control (role="status" on a soft surface,
 *              info glyph + text so colour is never the only signifier, NEVER red),
 *              with an optional retry. The toggle stays usable.
 *   - hover    a gentle track-border lift (no transform → reduced-motion safe).
 *   - focus    a focus-visible ring on the track (mockup parity).
 *   - disabled native `disabled`; dimmed, non-interactive (Onboarding preview rows).
 *
 * Self-contained per the implement plan: styles ship once via useInsertionEffect
 * under a namespaced `lsw-*` class set, every value `var(--token, fallback)` so it
 * renders standalone AND against lantern.css. Named + default export;
 * `data-component="Switch"` on the root.
 */

import React, { useId, useInsertionEffect, useState } from 'react';

/* ------------------------------------------------------------------ *
 * Rest hardening                                                      *
 * ------------------------------------------------------------------ */

/* Strip ASCII whitespace + control chars (code point <= 0x20, plus DEL) that
   browsers ignore inside URLs, so a scheme check cannot be slipped past with an
   embedded tab/newline (e.g. "java\tscript:"). Done char-by-char to avoid a
   control-char range in a regex literal. */
function stripUrlNoise(s) {
  let out = '';
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) continue;
    out += s[i];
  }
  return out;
}

const DANGEROUS_SCHEME = /^(javascript|vbscript|data):/;

/* Scrub URL-bearing / innerHTML keys from forwarded rest so a caller cannot
   smuggle a javascript: URL or raw HTML through the spread onto the root. Switch
   has no prop-supplied href/src of its own (it is a control), but a host may still
   pass arbitrary attributes — those are sanitised here before they reach the DOM. */
function safeRest(o) {
  if (!o || typeof o !== 'object') return o;
  const clean = (h) =>
    typeof h === 'string' && DANGEROUS_SCHEME.test(stripUrlNoise(h).toLowerCase())
      ? undefined
      : h;
  const r = { ...o };
  delete r.dangerouslySetInnerHTML;
  delete r.srcDoc;
  delete r.srcdoc;
  for (const k of ['href', 'src', 'action', 'formAction', 'poster']) {
    if (k in r) r[k] = clean(r[k]);
  }
  return r;
}

/* ------------------------------------------------------------------ *
 * Value helpers                                                       *
 * ------------------------------------------------------------------ */

/** True when a value is renderable and not "empty" (strings are trimmed). */
function isPresent(value) {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
}

/**
 * Resolve a user-facing error string from a string | true | Error | error-like
 * object, falling back to `fallback`. false / null / undefined → null (no error).
 * Never renders a bare boolean ("true"/"false") as visible text.
 */
function resolveError(error, fallback) {
  if (error == null || error === false) return null;
  if (error === true) return fallback;
  let raw;
  if (typeof error === 'string') raw = error;
  else if (error instanceof Error) raw = error.message;
  else if (typeof error === 'object') raw = error.message != null ? String(error.message) : '';
  else raw = String(error);
  return raw.trim() || fallback;
}

/* ------------------------------------------------------------------ *
 * Icon (self-contained, no external asset)                            *
 * ------------------------------------------------------------------ */

/** A calm circle-i for the error note — never an alarm triangle. */
function InfoGlyph({ size = 16 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12" y2="8" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Styles                                                              *
 * ------------------------------------------------------------------ */

const STYLE_ID = 'lant-switch-styles';
const STYLES = `
.lsw {
  /* component-scoped knobs (self-defined so the component themes standalone) */
  --lsw-track-w: 44px;
  --lsw-track-h: 24px;
  --lsw-thumb: 18px;
  --lsw-pad: 3px;
  --lsw-travel: calc(var(--lsw-track-w) - var(--lsw-thumb) - (2 * var(--lsw-pad)));
  --lsw-on: var(--moss-500, oklch(0.68 0.14 150));
  --lsw-off-track: var(--surface-3, #e7e2d6);
  --lsw-off-thumb: var(--text-mid, #424a66);
  --lsw-line: var(--line, rgb(29 34 51 / 0.18));
  --lsw-ring: var(--accent, oklch(0.54 0.11 220));

  display: inline-flex;
  flex-direction: column;
  gap: var(--s-2, 8px);
  font-family: var(--font-ui, "Inter", -apple-system, "Segoe UI", sans-serif);
  color: var(--text-hi, #1d2233);
}
.lsw--block { display: flex; }

.lsw__row {
  display: flex;
  align-items: center;
  gap: var(--s-3, 12px);
}
.lsw--block .lsw__info { flex: 1 1 auto; }

.lsw__info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.lsw__label {
  font-weight: 600;
  font-size: var(--fs-sm, 0.92rem);
  color: var(--text-hi, #1d2233);
  cursor: pointer;
}
.lsw__req { color: var(--accent-text, var(--accent, #b06a1f)); margin-left: 2px; }
.lsw__desc {
  font-size: var(--fs-xs, 0.78rem);
  color: var(--text-low, #6b7390);
  margin-top: 1px;
}
.lsw__state {
  font: 600 var(--fs-xs, 0.78rem) var(--font-mono, ui-monospace, monospace);
  color: var(--text-low, #6b7390);
  min-width: 26px;
  text-align: right;
}

/* ---- the control: native input layered over a track + sliding thumb ---- */
.lsw__control {
  position: relative;
  display: inline-flex;
  flex: none;
  width: var(--lsw-track-w);
  height: var(--lsw-track-h);
}
.lsw__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.lsw__input:disabled { cursor: not-allowed; }
.lsw__track {
  width: var(--lsw-track-w);
  height: var(--lsw-track-h);
  border-radius: 999px;
  background: var(--lsw-off-track);
  border: 1px solid var(--lsw-line);
  box-sizing: border-box;
  position: relative;
  pointer-events: none;
  transition: background var(--motion-quick, 150ms) var(--ease-out-soft, ease),
    border-color var(--motion-quick, 150ms) var(--ease-out-soft, ease);
}
.lsw__thumb {
  position: absolute;
  top: var(--lsw-pad);
  left: var(--lsw-pad);
  width: var(--lsw-thumb);
  height: var(--lsw-thumb);
  border-radius: 50%;
  background: var(--lsw-off-thumb);
  transition: transform var(--motion-quick, 150ms) var(--ease-out-soft, ease),
    background var(--motion-quick, 150ms) var(--ease-out-soft, ease);
}

/* checked — thumb travels right; track turns calm moss green */
.lsw__input:checked + .lsw__track {
  background: var(--lsw-on);
  border-color: transparent;
}
.lsw__input:checked + .lsw__track .lsw__thumb {
  transform: translateX(var(--lsw-travel));
  background: #fff;
}

/* hover (spec listed missing) — a gentle border lift, no transform */
.lsw__input:not(:disabled):hover + .lsw__track { border-color: var(--lsw-ring); }

/* focus-visible ring (mockup parity) */
.lsw__input:focus-visible + .lsw__track {
  outline: 3px solid var(--lsw-ring);
  outline-offset: 2px;
}

/* disabled — dimmed, never alarming */
.lsw__input:disabled + .lsw__track { opacity: 0.5; }
.lsw[data-disabled="true"] .lsw__label { cursor: default; color: var(--text-low, #6b7390); }

/* ---- empty (calm placeholder, never blank) ---- */
.lsw__empty {
  font-size: var(--fs-xs, 0.78rem);
  color: var(--text-low, #6b7390);
  font-style: italic;
}

/* ---- loading skeleton (calm shimmer pill, never a spinner) ---- */
.lsw__skel {
  flex: none;
  width: var(--lsw-track-w);
  height: var(--lsw-track-h);
  border-radius: 999px;
  background: var(--surface-3, #e7e2d6);
  border: 1px solid var(--lsw-line);
  box-sizing: border-box;
}

/* ---- error note (CALM — role=status on a soft surface, NEVER red) ---- */
.lsw__error {
  display: flex;
  align-items: flex-start;
  gap: var(--s-2, 8px);
  padding: var(--s-2, 8px) var(--s-3, 12px);
  border-radius: var(--r-sm, 8px);
  background: var(--surface-2, #f1ece1);
  border: 1px solid var(--lsw-line);
  font-size: var(--fs-xs, 0.78rem);
  color: var(--text-mid, #424a66);
}
.lsw__error-ic { flex: none; display: inline-flex; color: var(--text-mid, #424a66); margin-top: 1px; }
.lsw__error-msg { flex: 1 1 auto; min-width: 0; }
.lsw__retry {
  flex: none;
  appearance: none;
  border: 1px solid var(--lsw-line);
  background: transparent;
  color: var(--accent-text, var(--accent, #b06a1f));
  font: 600 var(--fs-xs, 0.78rem) var(--font-ui, sans-serif);
  border-radius: var(--r-sm, 8px);
  padding: 2px 10px;
  cursor: pointer;
  transition: background var(--motion-quick, 150ms) var(--ease-out-soft, ease);
}
.lsw__retry:hover { background: var(--surface-3, #e7e2d6); }
.lsw__retry:focus-visible { outline: 3px solid var(--lsw-ring); outline-offset: 2px; }

/* visually-hidden but available to assistive tech */
.lsw__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ---- reduced motion: neutralise the slide + any hover/active transitions ---- */
@media (prefers-reduced-motion: reduce) {
  .lsw__track,
  .lsw__thumb,
  .lsw__retry { transition: none; }
}

/* ---- forced colors / Windows High Contrast ----
   The colour-fill track + box-shadow ring vanish under forced-colors, so re-assert a
   real border + system-colour signal. State stays conveyed by the thumb POSITION and
   a Highlight fill (colour is never the only signifier), paired with role=switch and
   the optional on/off text. */
@media (forced-colors: active) {
  .lsw__track { background: Canvas; border: 1px solid CanvasText; }
  .lsw__thumb { background: CanvasText; }
  .lsw__input:checked + .lsw__track { background: Canvas; border-color: Highlight; }
  .lsw__input:checked + .lsw__track .lsw__thumb { background: Highlight; }
  .lsw__input:focus-visible + .lsw__track { outline: 2px solid Highlight; }
  .lsw__skel { border: 1px solid CanvasText; }
  .lsw__error { border: 1px solid CanvasText; }
  .lsw__retry:focus-visible { outline: 2px solid Highlight; }
}

/* AD-08 touch-target floor (tokens.json targets.minimumCssPx = 44). Below the rail breakpoint
   every control here meets 44 CSS px; desktop geometry is unchanged. Measured in Chromium at 375
   before and after: docs/reviews/p05-p10/jobs/p05-23-fable-02/evidence. */
@media (max-width: 1023.98px) {
  /* the native input IS the switch; centre it on the track and let it grow to the floor */
  .lsw__input {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    width: max(100%, var(--target-min, 44px));
    height: max(100%, var(--target-min, 44px));
    transform: translate(-50%, -50%);
  }
}
`;

/** Inject the component's scoped stylesheet once per document (SSR-guarded, deduped). */
function useSwitchStyles() {
  useInsertionEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(STYLE_ID)) return undefined;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
    return undefined;
  }, []);
}

/* ------------------------------------------------------------------ *
 * Component                                                           *
 * ------------------------------------------------------------------ */

/**
 * Switch — a calm on/off toggle, built on a native checkbox with role="switch".
 *
 * @param {Object} props
 * @param {string|React.ReactNode} [props.label]  Visible label rendered beside the
 *        toggle (clicking it toggles the control via htmlFor). When present, it
 *        becomes the accessible name; otherwise pass `ariaLabel`.
 * @param {string|React.ReactNode} [props.description]  Helper text under the label,
 *        wired to the input via aria-describedby.
 * @param {boolean} [props.checked]  Controlled on/off value. When provided the
 *        component is controlled — pair with `onChange`/`onCheckedChange`.
 * @param {boolean} [props.defaultChecked=false]  Initial value when uncontrolled.
 * @param {(checked: boolean, event)=>void} [props.onCheckedChange]  Called with the
 *        next boolean (and the event) on toggle — the ergonomic handler.
 * @param {(event)=>void} [props.onChange]  Raw change handler (composed with
 *        onCheckedChange; both fire).
 * @param {boolean} [props.disabled=false]  Non-interactive, dimmed (e.g. previews).
 * @param {boolean} [props.required=false]  Marks the input required + a visible "*".
 * @param {boolean} [props.showStateText=false]  Show an "On"/"Off" word before the
 *        control (decorative — aria-hidden; the role=switch state is the source of truth).
 * @param {string} [props.onText="On"]  On-state word for `showStateText`.
 * @param {string} [props.offText="Off"]  Off-state word for `showStateText`.
 * @param {boolean} [props.loading=false]  Skeleton/busy state (highest precedence).
 * @param {string} [props.loadingLabel="Loading this setting…"]  Polite SR busy text.
 * @param {boolean} [props.empty=false]  Render a calm placeholder instead of the
 *        control (no setting to show).
 * @param {string} [props.emptyText="No setting to show"]  Placeholder copy.
 * @param {string|Error|object|boolean} [props.error]  Calm inline error note (NEVER
 *        red). The toggle stays usable; announced politely.
 * @param {string} [props.errorLabel="We couldn't save this setting."]  Used when
 *        `error` is truthy but carries no message of its own.
 * @param {(event)=>void} [props.onRetry]  Adds a quiet retry control to the note.
 * @param {string} [props.retryLabel="Try again"]  Retry control label.
 * @param {string} [props.ariaLabel]  Accessible name for the input when there is no
 *        visible `label` (or an explicit override).
 * @param {string} [props.id]  Input id (generated when omitted).
 * @param {string} [props.name]  Input name (form submission).
 * @param {string} [props.value]  Input value (form submission).
 * @param {string} [props.className]  Extra classes for the root element.
 * @param {React.Ref} [props.ref]  Ref forwarded to the root element (React 19 ref prop).
 * @param {React.Ref} [props.innerRef]  Alias for `ref`.
 * @param {React.Ref} [props.inputRef]  Ref forwarded to the underlying <input>.
 * @param {Object} [props.rest]  Forwarded to the root, spread BEFORE the
 *        component-owned identity attrs so they cannot be clobbered.
 *
 * Data hooks: `data-component="Switch"`, `data-state`
 * ("loading"|"empty"|"error"|"default"), `data-checked`, `data-disabled`.
 */
export function Switch({
  label,
  description,
  checked,
  defaultChecked = false,
  onCheckedChange,
  onChange,
  disabled = false,
  required = false,
  showStateText = false,
  onText = 'On',
  offText = 'Off',
  loading = false,
  loadingLabel = 'Loading this setting…',
  empty = false,
  emptyText = 'No setting to show',
  error = null,
  errorLabel = "We couldn't save this setting.",
  onRetry,
  retryLabel = 'Try again',
  ariaLabel,
  id,
  name,
  value,
  className = '',
  ref,
  innerRef,
  inputRef,
  ...rest
}) {
  useSwitchStyles();
  const baseId = useId();
  const inputId = isPresent(id) ? id : `${baseId}-input`;
  const descId = `${baseId}-desc`;
  const errId = `${baseId}-err`;
  const srId = `${baseId}-status`;

  // ---- controlled / uncontrolled ----
  // CR-06: ONE authoritative current value. The <input> is unmounted for the
  // loading and empty states and recreated afterwards; if it were seeded from
  // `defaultChecked` on that remount, the native `checked` would silently reset
  // while the visual state (internalChecked) kept saying "On". So the input is
  // always rendered from `isOn` — the prop when controlled, our own state when
  // uncontrolled — and `defaultChecked` only seeds that state once.
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = useState(!!defaultChecked);
  const isOn = isControlled ? !!checked : internalChecked;

  const handleChange = (event) => {
    const next = event.target.checked;
    if (!isControlled) setInternalChecked(next);
    if (typeof onCheckedChange === 'function') onCheckedChange(next, event);
    if (typeof onChange === 'function') onChange(event);
  };

  // ---- state precedence: loading > empty > (default | error overlay) ----
  const isBusy = !!loading;
  const isEmpty = !isBusy && !!empty;
  const errorMessage = !isBusy && !isEmpty ? resolveError(error, errorLabel) : null;
  const showError = !!errorMessage;
  const dataState = isBusy ? 'loading' : isEmpty ? 'empty' : showError ? 'error' : 'default';

  // ---- accessible name / labelling ----
  const hasVisibleLabel = isPresent(label);
  const hasInfo = hasVisibleLabel || isPresent(description);
  // input aria-label: needed when there is no visible label; an explicit ariaLabel
  // always wins (overrides the wrapped/associated text). Fallback keeps the control
  // from ever being unnamed (a hard axe failure).
  let inputAriaLabel;
  if (isPresent(ariaLabel)) inputAriaLabel = ariaLabel;
  else if (!hasVisibleLabel) inputAriaLabel = 'Toggle setting';

  const describedBy =
    [isPresent(description) ? descId : null, showError ? errId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  // ---- root props (rest FIRST → component-owned identity attrs win) ----
  const rootProps = safeRest({ ...rest });
  const rootClass = ['lsw', hasInfo ? 'lsw--block' : null, className].filter(Boolean).join(' ');
  const setRef = ref || innerRef;

  const info = hasInfo ? (
    <span className="lsw__info" data-part="info">
      {hasVisibleLabel ? (
        <label className="lsw__label" htmlFor={inputId} data-part="label">
          {label}
          {required ? (
            <>
              <span className="lsw__req" aria-hidden="true">
                *
              </span>
              <span className="lsw__sr"> (required)</span>
            </>
          ) : null}
        </label>
      ) : null}
      {isPresent(description) ? (
        <span className="lsw__desc" id={descId} data-part="description">
          {description}
        </span>
      ) : null}
    </span>
  ) : null;

  const control = (
    <span className="lsw__control" data-part="control">
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        role="switch"
        className="lsw__input"
        name={name}
        value={value}
        disabled={disabled}
        required={required || undefined}
        checked={isOn}
        onChange={handleChange}
        aria-label={inputAriaLabel}
        aria-describedby={describedBy}
      />
      <span className="lsw__track" aria-hidden="true">
        <span className="lsw__thumb" />
      </span>
    </span>
  );

  return (
    /* root is a <div> (not <span>): it carries block-level rows + the error note,
       which are invalid inside a phrasing <span> and would auto-close it under SSR /
       the HTML parser. `.lsw` keeps display:inline-flex so it still sits inline. */
    <div
      {...rootProps}
      ref={setRef}
      className={rootClass}
      data-component="Switch"
      data-state={dataState}
      data-checked={isOn ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : undefined}
      aria-busy={isBusy ? 'true' : undefined}
    >
      {isBusy ? (
        <div className="lsw__row" data-part="loading">
          {info}
          <span className="lsw__skel" aria-hidden="true" />
        </div>
      ) : isEmpty ? (
        <div className="lsw__row" data-part="empty">
          {info}
          <span className="lsw__empty">{emptyText}</span>
        </div>
      ) : (
        <>
          <div className="lsw__row">
            {info}
            {showStateText ? (
              <span className="lsw__state" aria-hidden="true" data-part="state">
                {isOn ? onText : offText}
              </span>
            ) : null}
            {control}
          </div>
          {showError ? (
            /* calm note — neutral on purpose (Settings never panics), NEVER red */
            <div className="lsw__error" data-part="error" role="status" id={errId}>
              <span className="lsw__error-ic">
                <InfoGlyph />
              </span>
              <span className="lsw__error-msg">{errorMessage}</span>
              {typeof onRetry === 'function' ? (
                <button
                  type="button"
                  className="lsw__retry"
                  data-part="retry"
                  onClick={(event) => {
                    onRetry(event);
                  }}
                >
                  {retryLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {/* polite live region for the LOADING announcement only — the skeleton has no
          visible text, so this is its announcement path. The error is NOT duplicated
          here: the visible error note already carries role="status" (an implicit
          polite region) and is wired via aria-describedby, so announcing it here too
          would double-speak it. Always mounted so its text node mutates in place. */}
      <span className="lsw__sr" id={srId} aria-live="polite" data-part="status">
        {isBusy ? loadingLabel : ''}
      </span>
    </div>
  );
}

export default Switch;
